import { SendOutlined } from '@ant-design/icons';
import { Modal, ModalProps, Select } from 'antd';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import useAPIQuery from '../api/useAPIQuery';
import useInfiniteAPIQuery from '../api/useInfiniteAPIQuery';
import usePrefetchPages from '../hooks/usePrefetchPages';
import { profileKeys } from '../pages/EditOpportunity/ProfileForm';
import { JobPostingDetail } from '../types';
import EmailForm, { EmailFormValues } from './EmailForm';
import Form from './Form';
import { fixUnorderedListInQuill } from './RichEditor/RichEditor';
import TestsForm, { TestsFormValues } from './TestsForm';

export interface SendTestsModalProps extends ModalProps {
  jobPostingId?: JobPostingDetail['id'];
  helmetTitle: string;
  onFinish?: (values: TestsFormValues & EmailFormValues) => void;
  loading?: boolean;
}

const SendTestsModal = ({
  jobPostingId,
  helmetTitle,
  onFinish,
  loading,
  ...props
}: SendTestsModalProps) => {
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const functionIdValue = Form.useWatch('functionId', form);

  const jobPosting = useAPIQuery(`/job-postings/${jobPostingId}/`, { enabled: !!jobPostingId });

  const config = useAPIQuery('/configs/tenant-configurations/');

  const user = useAPIQuery('/users/me/');

  const functions = useAPIQuery(['/mindsight/ats-integrations/functions/']);

  const emailCommunicationTemplatesPages = useInfiniteAPIQuery([
    '/email-communication-templates/',
    {
      params: {
        type: 'TESTS',
        individual: false,
      },
    },
  ]);

  const emailCommunicationTemplates = usePrefetchPages(
    emailCommunicationTemplatesPages,
    (data) => data.results
  );

  return (
    <Modal
      okText={t('Enviar')}
      okButtonProps={{ htmlType: 'submit', icon: <SendOutlined />, loading }}
      onOk={() => form.submit()}
      {...props}
    >
      <Helmet>
        <title>
          {helmetTitle} | {t('Recrutamento e Seleção')}
        </title>
      </Helmet>
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          functionId: jobPosting.data?.function_id,
          testTypeList: jobPosting.data?.function_id
            ? [
                ...(functions.data?.results
                  .find(({ id }) => id === jobPosting.data?.function_id)
                  ?.required_assessments.map(({ key }) => key) || []),
                'OCP',
              ]
            : ['OCP'],
          email_communication_template: null,
          subject: '',
          message: '',
          fromName: config.data?.companyFromName || '',
          replyTo: user.data?.email,
          enableReplyTo: false,
          attachments: [],
        }}
        onFinish={(variables) =>
          onFinish?.({ ...variables, message: fixUnorderedListInQuill(variables.message) })
        }
      >
        <Form.Item
          name="functionId"
          label={`${t('Selecione')}:`}
          hidden={!!jobPosting.data?.function_id}
        >
          <Select>
            {functions.data?.results
              .filter(({ name }) => profileKeys.includes(name))
              .map(({ id, name }) => (
                <Select.Option key={id} value={id}>
                  {name}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>
        <TestsForm
          tests={
            functions.data?.results
              .find(({ id }) => id === functionIdValue)
              ?.required_assessments?.map(({ key }) => key) || []
          }
          showNexa={config.data?.enableNexaAssessment}
          requireTests
        />

        <EmailForm
          attachments
          customVariables={[
            { value: '[link_testes]', label: t('Link dos Testes') },
            { value: '[candidato]', label: t('Nome do Candidato') },
            { value: '[empresa]', label: t('Nome da Empresa') },
            ...(jobPostingId ? [{ value: '[vaga]', label: t('Nome da Vaga') }] : []),
          ]}
          image
          richEditorRules={[
            { message: 'Campo obrigatório.', required: true },
            {
              validator: (_, value: string) => {
                if (value && (value.includes('[link_testes]') || value.includes('[LINK_TESTS]'))) {
                  return Promise.resolve();
                }

                return Promise.reject(
                  new Error(
                    t(
                      'Para enviar os testes é necessário incluir a variável "Link dos Testes" no corpo do e-mail.'
                    )
                  )
                );
              },
            },
            {
              validator(_, value) {
                const variablesRegex = /\[vaga\]/;
                if (!jobPostingId && variablesRegex.test(value)) {
                  return Promise.reject(
                    new Error(
                      t(
                        'Não é possível utilizar a variável [vaga] ao enviar email pelo Banco de Talentos'
                      )
                    )
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
          templates={emailCommunicationTemplates.map((emailCommunicationTemplate) => ({
            id: emailCommunicationTemplate.id,
            name: emailCommunicationTemplate.name,
            message: emailCommunicationTemplate.message,
            subject: emailCommunicationTemplate.subject,
            fromName: emailCommunicationTemplate.from_name,
            attachments: emailCommunicationTemplate.attachments,
          }))}
          fromName
          replyTo
        />
      </Form>
    </Modal>
  );
};

export default SendTestsModal;
